// CvScoringSettings — the explained CV score (Nilay Sharma's proposal
// pr_7c4a64b070): which score orders the CV lists ('ai', as before, until the
// school switches) and the school's preferred institutions, matched on where
// a candidate taught or did their B.Ed. Read by HR; changed by a recruitment
// administrator, checked first (GET / PUT /api/cv-scoring/settings).
// @story HIR-HR25

import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { Button, Select } from '../../ds';
import { Loading, Note } from '../../components/page';

const ORDER = [
  { value: 'ai', label: "The AI's CV score (as now)" },
  { value: 'explained', label: 'The explained CV score' },
];

function isAdmin() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u.role === 'admin' || (u.permissions || []).includes('recruitment:admin');
  } catch { return false; }
}

export default function CvScoringSettings() {
  const admin = isAdmin();
  const [st, setSt] = useState({ phase: 'loading' });
  const [orderBy, setOrderBy] = useState('ai');
  const [list, setList] = useState('');
  const [preview, setPreview] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = () => api.get('/cv-scoring/settings')
    .then((r) => {
      const d = r.data.data;
      setSt({ phase: 'ready', ...d });
      setOrderBy(d.orderBy);
      setList((d.preferredInstitutions || []).map((p) => [p.name, ...(p.aliases || [])].join(' | ')).join('\n'));
    })
    .catch(() => setSt({ phase: 'error' }));
  useEffect(() => { load(); }, []);

  const body = () => ({
    orderBy,
    preferredInstitutions: list.split('\n').map((l) => l.split('|').map((x) => x.trim()).filter(Boolean)).filter((p) => p.length)
      .map(([name, ...aliases]) => ({ name, aliases })),
  });
  const check = () => {
    setMsg(null);
    api.put('/cv-scoring/settings', { ...body(), preview: true })
      .then((r) => setPreview(r.data.data))
      .catch((e) => setMsg({ tone: 'danger', text: e?.response?.data?.message || 'Could not check that.' }));
  };
  const save = () => api.put('/cv-scoring/settings', body())
    .then(() => { setPreview(null); setMsg({ tone: 'success', text: 'Saved.' }); load(); })
    .catch((e) => setMsg({ tone: 'danger', text: e?.response?.data?.message || 'Could not save.' }));

  if (st.phase === 'loading') return <Loading label="Reading the CV scoring" />;
  if (st.phase === 'error') return <Note tone="warning">The CV scoring could not be read just now. Reload the page to try again.</Note>;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <p style={{ margin: 0, color: 'var(--text-2)' }}>
        The explained CV score shows how each candidate's score is built: subject and grade fit, qualification, experience, stability and
        preferred institution. A part the candidate's form never asked about is left out, never counted as zero. Its weights (SCORING_TEACHING v4)
        are provisional until the CHRM signs them off.
      </p>
      <Select label="Order the CV lists by" value={orderBy} disabled={!admin} options={ORDER} onChange={(v) => { setOrderBy(v); setPreview(null); }} />
      <label style={{ display: 'grid', gap: 6, fontSize: 'var(--fs-body-sm)' }}>
        Preferred institutions — one a line; other names after a | (e.g. "Delhi Public School | DPS")
        <textarea aria-label="Preferred institutions" rows={5} value={list} disabled={!admin}
          onChange={(e) => { setList(e.target.value); setPreview(null); }}
          style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 6, padding: 8, font: 'inherit' }} />
      </label>
      {preview && <Note tone="info">This changes: {preview.changes.map((c) => c.what).join(', ')}. Nobody's status changes.</Note>}
      {admin && <div>{preview ? <Button onClick={save}>Save</Button> : <Button variant="secondary" onClick={check}>Check</Button>}</div>}
      {!admin && <Note tone="info">Only a recruitment administrator can change this.</Note>}
      {st.changedAt && <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' }}>Last changed {new Date(st.changedAt).toLocaleDateString('en-IN')}{st.changedBy ? ` by ${st.changedBy}` : ''}</div>}
      {msg && <Note tone={msg.tone}>{msg.text}</Note>}
    </div>
  );
}
